<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            {{ model.id ? 'Edit User' : 'Add User to Group' }}
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="model.id" :closeable="false">
                Editing user <strong class="font-bold">{{ model.email }}</strong>
            </vx-alert>
            <vx-alert v-else :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                </template>
                Adding user to <strong v-if="props.groupName" class="font-bold">{{ props.groupName }}</strong> group
            </vx-alert>
            <vx-alert v-if="!model.id && willUseSso" color="info" :closeable="false" data-test="sso-invite-hint">
                They'll log in with SSO.
            </vx-alert>

            <vx-alert
                v-if="changingOwner && owner"
                data-test="changing-owner-alert"
                :closeable="false"
                color="danger"
            >
                The current owner of <strong>{{ props.groupName }}</strong> is <strong>{{ owner.name }}</strong
                >. By changing this user to an owner, you will transfer ownership to them.
            </vx-alert>

            <div v-if="!model.id">
                <vx-label>Email</vx-label>

                <vx-selectable
                    :options="users"
                    :loading="searching"
                    relative-dropdown
                    :reduce="(option) => option.email"
                    :createOption="
                        (option) =>
                            typeof option === 'string'
                                ? {
                                      email: option,
                                  }
                                : { ...option }
                    "
                    @search="search"
                    v-model="model.email"
                    data-test="email"
                    label="email"
                    size="lg"
                    taggable
                    :error="inviteBlocked ? blockedMessage : displayFirstError(validate.email.$errors)"
                >
                </vx-selectable>
            </div>
            <div>
                <vx-label>
                    Group Role
                    <template v-slot:description>
                        View
                        <a :href="permissionChartLink" target="_blank" rel="noopener noreferrer">permission chart</a>
                        to see what each type has access to.
                    </template>
                </vx-label>

                <vx-selectable
                    v-model="model.role"
                    size="lg"
                    data-test="select-role"
                    relative-dropdown
                    :reduce="(option) => option.value"
                    :options="groupRolesOptions()"
                    :clearable="false"
                    :error="displayFirstError(validate.role.$errors)"
                >
                </vx-selectable>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit()"
                    :loading="submitting"
                    :disabled="inviteBlocked"
                    data-test="submit-btn"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Add' }}
                    <font-awesome-icon widthAuto :icon="model.id ? faCircleCheck : faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { VxAlert, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { groupInvitationsClient, groupUsersClient, usersClient } from '~/services';
import { HTTP_CREATED, HTTP_OK } from '~/support/HttpStatuses';
import { faCircleCheck, faCirclePlus, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DEFAULT_GROUP_ROLE, groupRolesOptions } from '~/constants/roles';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { email, required, requiredIf } from '@vuelidate/validators';
import debounce from '~/utils/debounce';
import { permissionChartLink } from '~/constants/groups';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useVerifiedEmailDomains } from '~/composables/useVerifiedEmailDomains';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
    groupName: {
        type: String,
        required: false,
    },
});

const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate(
    {
        email: {
            requiredIf: requiredIf(() => model.value.id === null),
            email: email,
        },
        role: {
            required: required,
        },
    },
    computed(() => ({ role: model.value.role, email: model.value.email })),
    { $externalResults }
);

const emit = defineEmits(['added', 'updated', 'invited']);

const submitting = ref(false);
const users = ref([]);
const searching = ref(false);
const search = debounce((search) => {
    searching.value = true;
    usersClient
        .listUsers(props.teamId, {
            query: search,
        })
        .then((response) => {
            users.value = response.data.data;
            searching.value = false;
        });
}, 200);

const { blocked, blockedMessage, willUseSso } = useVerifiedEmailDomains(
    props.teamId,
    computed(() => model.value.email ?? '')
);
// the verified-domain rule applies to invitations only; a known team member is added with an upsert
const inviteBlocked = computed(
    () => !model.value.id && !users.value.some((user) => user.email === model.value.email) && blocked.value
);

const updateVisible = (isVisible) => {
    validate.value.$reset();
    visible.value = isVisible;

    if (!isVisible) {
        model.value = {
            id: null,
            name: '',
            email: '',
            role: DEFAULT_GROUP_ROLE,
        };
    }
};

onMounted(() => {
    if (!model.value?.id) {
        search();
    }
});

watch(visible, () => {
    if (visible.value) {
        getGroupOwner();
    }
})

const submit = async () => {
    if (inviteBlocked.value) {
        return;
    }

    const isValid = await validateRules(validate);
    if (!isValid || submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const existingUser = model.value?.id
            ? model.value
            : users.value.find((user) => user.email === model.value.email);

        if (existingUser?.id) {
            const response = await groupUsersClient.upsertGroupUser(props.teamId, props.groupId, existingUser.id, {
                role: model.value.role,
            });

            if (response.status === HTTP_CREATED) {
                emit('added', { ...model.value, id: existingUser.id, name: existingUser.name });
                toasted.success('New User added successfully');
            } else if (response.status === HTTP_OK) {
                emit('updated', { ...model.value, id: existingUser.id, name: existingUser.name });
                toasted.success('User updated successfully');
            }
        } else {
            await groupInvitationsClient.sendInvitation(props.teamId, props.groupId, {
                email: model.value.email,
                role: model.value.role,
            });

            emit('invited', { ...model.value });
            toasted.success('New User invited successfully');
        }

        updateVisible(false);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

const owner = ref();

const isGroupOwner = computed(() => {
    return owner.value?.id === model.value.id
})

const changingOwner = computed(() => {
    if (!isGroupOwner.value && model.value.role === 'owner') {
        return true;
    }

    return false;
})

const getGroupOwner = async () => {
    try {
        const response = await groupUsersClient.listGroupUsers(props.teamId, props.groupId, {
            role: 'owner',
        });

        owner.value = response.data?.data?.[0]
    } catch (e) {
        console.error(catchRequestError(e));
    }
}
</script>
